/**
 * Theme JavaScript — Custom Development Theme
 * Handles: mobile menu, product variants, cart, and accessibility.
 */

(function () {
  'use strict';

  // --------------------------------------------------------------------------
  // Mobile Menu
  // --------------------------------------------------------------------------
  function initMobileMenu() {
    var toggle = document.querySelector('[data-menu-toggle]');
    var closeBtn = document.querySelector('[data-menu-close]');
    var mobileMenu = document.querySelector('[data-mobile-menu]');
    var overlay = document.querySelector('[data-menu-overlay]');

    if (!toggle || !mobileMenu) return;

    function openMenu() {
      mobileMenu.classList.add('header__mobile-menu--open');
      if (overlay) overlay.classList.add('header__overlay--visible');
      document.body.style.overflow = 'hidden';
      toggle.setAttribute('aria-expanded', 'true');
      // Focus first link
      var firstLink = mobileMenu.querySelector('a');
      if (firstLink) firstLink.focus();
    }

    function closeMenu() {
      mobileMenu.classList.remove('header__mobile-menu--open');
      if (overlay) overlay.classList.remove('header__overlay--visible');
      document.body.style.overflow = '';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    }

    toggle.addEventListener('click', openMenu);

    if (closeBtn) closeBtn.addEventListener('click', closeMenu);
    if (overlay) overlay.addEventListener('click', closeMenu);

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && mobileMenu.classList.contains('header__mobile-menu--open')) {
        closeMenu();
      }
    });
  }

  // --------------------------------------------------------------------------
  // Product Variants
  // --------------------------------------------------------------------------
  function initProductVariants() {
    var form = document.getElementById('product-form');
    if (!form) return;

    var selects = form.querySelectorAll('.main-product__option-select');
    if (selects.length === 0) return;

    // Update price display when variant changes
    selects.forEach(function (select) {
      select.addEventListener('change', function () {
        // Find the matching variant from the product's variant data
        updateVariantFromOptions(form, selects);
      });
    });
  }

  function updateVariantFromOptions(form, selects) {
    // Build selected options string to match against variant titles
    var selectedOptions = [];
    selects.forEach(function (select) {
      selectedOptions.push(select.value);
    });

    var variantTitle = selectedOptions.join(' / ');

    // Look through variant option elements (data attributes set by Shopify)
    var variantInput = form.querySelector('[name="id"]');
    var variantOptions = form.querySelectorAll('[data-variant-id]');

    variantOptions.forEach(function (option) {
      if (option.getAttribute('data-variant-title') === variantTitle) {
        variantInput.value = option.getAttribute('data-variant-id');
      }
    });
  }

  // --------------------------------------------------------------------------
  // Cart
  // --------------------------------------------------------------------------
  function initCart() {
    var quantityInputs = document.querySelectorAll('[data-cart-quantity]');
    var form = document.querySelector('.main-cart__form');

    if (!form || quantityInputs.length === 0) return;

    // Auto-update cart when quantity changes (with debounce)
    quantityInputs.forEach(function (input) {
      var debounceTimer;

      input.addEventListener('change', function () {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(function () {
          form.submit();
        }, 800);
      });
    });
  }

  // --------------------------------------------------------------------------
  // Quantity Input Validation
  // --------------------------------------------------------------------------
  function initQuantityInputs() {
    var inputs = document.querySelectorAll('input[type="number"]');

    inputs.forEach(function (input) {
      input.addEventListener('input', function () {
        var val = parseInt(input.value, 10);
        if (isNaN(val) || val < 1) {
          input.value = 1;
        }
      });
    });
  }

  // --------------------------------------------------------------------------
  // Smooth scroll for anchor links
  // --------------------------------------------------------------------------
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var target = document.querySelector(this.getAttribute('href'));
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  // --------------------------------------------------------------------------
  // External links — add rel="noopener" if missing
  // --------------------------------------------------------------------------
  function initExternalLinks() {
    document.querySelectorAll('a[target="_blank"]').forEach(function (link) {
      if (!link.getAttribute('rel')) {
        link.setAttribute('rel', 'noopener');
      }
    });
  }

  // --------------------------------------------------------------------------
  // Image lazy loading fallback (for browsers without native lazy)
  // --------------------------------------------------------------------------
  function initLazyImages() {
    if ('loading' in HTMLImageElement.prototype) return;

    var images = document.querySelectorAll('img[loading="lazy"]');
    if (!images.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var img = entry.target;
          img.src = img.dataset.src || img.src;
          observer.unobserve(img);
        }
      });
    });

    images.forEach(function (img) {
      observer.observe(img);
    });
  }

  // --------------------------------------------------------------------------
  // FAQ Accordion
  // --------------------------------------------------------------------------
  function initFaqAccordion() {
    var buttons = document.querySelectorAll('[data-faq-toggle]');
    if (!buttons.length) return;

    buttons.forEach(function (button) {
      button.addEventListener('click', function () {
        var expanded = this.getAttribute('aria-expanded') === 'true';
        var answer = document.getElementById(this.getAttribute('aria-controls'));
        if (!answer) return;

        // Close all others
        buttons.forEach(function (btn) {
          if (btn !== button) {
            btn.setAttribute('aria-expanded', 'false');
            var otherAnswer = document.getElementById(btn.getAttribute('aria-controls'));
            if (otherAnswer) otherAnswer.classList.remove('faq__answer--open');
          }
        });

        // Toggle clicked item
        if (expanded) {
          this.setAttribute('aria-expanded', 'false');
          answer.classList.remove('faq__answer--open');
        } else {
          this.setAttribute('aria-expanded', 'true');
          answer.classList.add('faq__answer--open');
        }
      });
    });
  }

  // --------------------------------------------------------------------------
  // Announcement Bar — rotation & close
  // --------------------------------------------------------------------------
  function initAnnouncementBar() {
    var bars = document.querySelectorAll('[data-section-type="announcement-bar"]');
    bars.forEach(function (bar) {
      var messages = bar.querySelectorAll('[data-announcement-message]');
      if (messages.length <= 1) return;

      var index = 0;
      var interval = parseInt(bar.getAttribute('data-rotation-interval'), 10) || 5000;
      var timer = null;

      function show(i) {
        messages.forEach(function (msg, idx) {
          msg.style.display = idx === i ? '' : 'none';
        });
      }

      function start() {
        timer = setInterval(function () {
          index = (index + 1) % messages.length;
          show(index);
        }, interval);
      }

      show(0);
      start();

      // Navigation buttons
      var prevBtn = bar.querySelector('[data-announcement-prev]');
      var nextBtn = bar.querySelector('[data-announcement-next]');
      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          clearInterval(timer);
          index = (index - 1 + messages.length) % messages.length;
          show(index);
          start();
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          clearInterval(timer);
          index = (index + 1) % messages.length;
          show(index);
          start();
        });
      }

      // Close button — persist via sessionStorage
      var closeBtn = bar.querySelector('[data-announcement-close]');
      if (closeBtn) {
        var storageKey = 'announcementBarClosed_' + (bar.getAttribute('data-announcement-bar') || 'default');
        if (sessionStorage.getItem(storageKey) === 'true') {
          bar.classList.add('announcement-bar--hidden');
        }
        closeBtn.addEventListener('click', function () {
          bar.classList.add('announcement-bar--hidden');
          sessionStorage.setItem(storageKey, 'true');
        });
      }
    });
  }

  // --------------------------------------------------------------------------
  // Customer Login — toggle between login and password recovery
  // --------------------------------------------------------------------------
  function initPasswordRecoveryToggle() {
    var recoverLink = document.getElementById('RecoverPassword');
    var cancelLink = document.querySelector('a[href="#login"]');
    var recoverForm = document.querySelector('.main-login__recover');
    var loginForm = document.querySelector('.main-login__form');

    if (!recoverLink || !recoverForm || !loginForm) return;

    function showRecover() {
      loginForm.style.display = 'none';
      recoverForm.classList.add('is-visible');
      var emailInput = recoverForm.querySelector('input[type="email"]');
      if (emailInput) emailInput.focus();
    }

    function showLogin() {
      recoverForm.classList.remove('is-visible');
      loginForm.style.display = '';
    }

    recoverLink.addEventListener('click', function (e) {
      e.preventDefault();
      showRecover();
    });

    if (cancelLink) {
      cancelLink.addEventListener('click', function (e) {
        e.preventDefault();
        showLogin();
      });
    }
  }

  // --------------------------------------------------------------------------
  // Customer Addresses — show/hide forms, delete, province selector
  // --------------------------------------------------------------------------
  function initAddressForms() {
    // "Add new address" button
    var addBtn = document.querySelector('[data-add-address]');
    var newForm = document.querySelector('[data-new-address-form]');

    if (addBtn && newForm) {
      addBtn.addEventListener('click', function () {
        newForm.hidden = false;
        addBtn.hidden = true;
        var firstInput = newForm.querySelector('input');
        if (firstInput) firstInput.focus();
      });

      // Cancel new address
      var cancelNewBtn = newForm.querySelector('[data-cancel-new-address]');
      if (cancelNewBtn) {
        cancelNewBtn.addEventListener('click', function () {
          newForm.hidden = true;
          addBtn.hidden = false;
        });
      }
    }

    // Edit existing address buttons
    var editBtns = document.querySelectorAll('.main-addresses__edit');
    editBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var addressId = this.getAttribute('data-address-id');
        var form = document.querySelector('[data-address-form="' + addressId + '"]');
        if (form) {
          form.hidden = false;
          var firstInput = form.querySelector('input');
          if (firstInput) firstInput.focus();
          // Initialize province for this edit form
          initProvince(addressId);
        }
      });
    });

    // Cancel edit address buttons
    var cancelBtns = document.querySelectorAll('[data-cancel-address]');
    cancelBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var form = this.closest('[data-address-form]');
        if (form) form.hidden = true;
      });
    });

    // Delete address buttons
    var deleteBtns = document.querySelectorAll('.main-addresses__delete');
    deleteBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var confirmMsg = this.getAttribute('data-confirm-message') || 'Delete this address?';
        if (confirm(confirmMsg)) {
          var addressId = this.getAttribute('data-address-id');
          var deleteForm = document.querySelector('[data-delete-form="' + addressId + '"]');
          if (deleteForm) deleteForm.submit();
        }
      });
    });
  }

  // --------------------------------------------------------------------------
  // Province selector — uses Shopify's shopify_common.js
  // --------------------------------------------------------------------------
  function initAllProvinces() {
    if (typeof Shopify === 'undefined' || !Shopify.CountryProvinceSelector) {
      // Fallback: show province fields as-is (selects will be empty)
      document.querySelectorAll('[data-province-container]').forEach(function (c) {
        c.hidden = false;
      });
      return;
    }

    // Initialize new address form province
    initProvince('new');

    // Initialize existing address edit form provinces
    document.querySelectorAll('[data-address-country]').forEach(function (country) {
      var key = country.getAttribute('data-address-country');
      if (key === 'new') return; // Already initialized above
      initProvince(key);
    });
  }

  function initProvince(key) {
    if (typeof Shopify === 'undefined' || !Shopify.CountryProvinceSelector) return;
    var country = document.querySelector('[data-address-country="' + key + '"]');
    var province = document.querySelector('[data-address-province="' + key + '"]');
    var container = document.querySelector('[data-province-container="' + key + '"]');
    if (!country || !province || !container) return;

    // Set default province before creating selector
    var defaultProvince = province.getAttribute('data-default');
    if (defaultProvince) province.value = defaultProvince;

    new Shopify.CountryProvinceSelector(country.id, province.id, {
      hideElement: container.id
    });
  }

  // --------------------------------------------------------------------------
  // Newsletter — client-side feedback (Shopify handles actual submit)
  // --------------------------------------------------------------------------
  function initNewsletterForms() {
    var forms = document.querySelectorAll('.newsletter__form');
    forms.forEach(function (form) {
      var input = form.querySelector('input[type="email"]');
      if (!input) return;

      form.addEventListener('submit', function () {
        var btn = form.querySelector('button[type="submit"]');
        if (btn) {
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
        }
      });
    });
  }

  // --------------------------------------------------------------------------
  // No-js class removal
  // --------------------------------------------------------------------------
  function removeNoJs() {
    document.documentElement.classList.remove('no-js');
    document.documentElement.classList.add('js');
  }

  // --------------------------------------------------------------------------
  // Cart Drawer, Quick View, and Privacy Banner Event Handlers
  // --------------------------------------------------------------------------
  function initCartDrawer() {
    var drawer = document.querySelector('[data-cart-drawer]');
    var overlay = document.querySelector('[data-cart-drawer-overlay]');
    var closeBtn = document.querySelector('[data-cart-drawer-close]');
    var cartTriggers = document.querySelectorAll('.header__icon--cart, [data-open-cart]');

    if (!drawer) return;

    function openDrawer(e) {
      if (e) e.preventDefault();
      drawer.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function closeDrawer() {
      drawer.setAttribute('aria-hidden', 'true');
      if (overlay) overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    cartTriggers.forEach(function (btn) {
      btn.addEventListener('click', openDrawer);
    });

    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);

    // Quantity +/- handlers
    drawer.addEventListener('click', function (e) {
      if (e.target.matches('[data-qty-plus]')) {
        var input = e.target.parentNode.querySelector('.cart-drawer-item__qty-input');
        if (input) {
          input.value = parseInt(input.value, 10) + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else if (e.target.matches('[data-qty-minus]')) {
        var input = e.target.parentNode.querySelector('.cart-drawer-item__qty-input');
        if (input && parseInt(input.value, 10) > 1) {
          input.value = parseInt(input.value, 10) - 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  function initQuickViewModal() {
    var modal = document.querySelector('[data-quick-view-modal]');
    var overlay = document.querySelector('[data-quick-view-overlay]');
    var closeBtn = document.querySelector('[data-quick-view-close]');

    if (!modal) return;

    function closeModal() {
      modal.setAttribute('aria-hidden', 'true');
      if (overlay) overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (overlay) overlay.addEventListener('click', closeModal);
  }

  function initPrivacyBanner() {
    var banner = document.querySelector('[data-privacy-banner]');
    if (!banner) return;

    var consent = localStorage.getItem('swiss_cookie_consent') || localStorage.getItem('swiss_privacy_accepted');
    if (consent) {
      banner.style.display = 'none';
      banner.hidden = true;
      return;
    }

    banner.style.display = 'block';
    banner.hidden = false;

    var acceptBtn = banner.querySelector('[data-privacy-accept]');
    var declineBtn = banner.querySelector('[data-privacy-decline]');

    if (acceptBtn) {
      acceptBtn.addEventListener('click', function () {
        localStorage.setItem('swiss_cookie_consent', 'accepted');
        localStorage.setItem('swiss_privacy_accepted', 'true');
        banner.style.display = 'none';
        banner.hidden = true;
      });
    }

    if (declineBtn) {
      declineBtn.addEventListener('click', function () {
        localStorage.setItem('swiss_cookie_consent', 'declined');
        localStorage.setItem('swiss_privacy_accepted', 'false');
        banner.style.display = 'none';
        banner.hidden = true;
      });
    }
  }

  function initCartToast() {
    var toast = document.querySelector('[data-cart-toast]');
    if (!toast) return;

    var closeBtn = toast.querySelector('[data-cart-toast-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        toast.setAttribute('aria-hidden', 'true');
      });
    }
  }

  var i18nDict = {
    EN: {
      navShop: "Catalog",
      navCategories: "Categories",
      navDirect: "In Stock",
      navDeals: "Offers",
      heroTitle: "NEW IN: ELECTRIC MOTORCYCLES",
      heroDesc: "Exclusively curated for you. All items in stock in Switzerland, ready for fast Swiss Post Priority delivery.",
      heroCta: "Shop Now",
      catalogTitle: "NEW IN: SWISS CATALOG",
      catalogPageTitle: "Products",
      catalogPageDesc: "All items in stock in Switzerland, ready for fast Swiss Post Priority delivery.",
      stockFilterLabel: "In Stock in Switzerland",
      sortLabel: "Sort by:",
      sortFeatured: "Featured",
      sortPriceAsc: "Price: Low to High (CHF)",
      sortPriceDesc: "Price: High to Low (CHF)",
      sortTitleAsc: "Alphabetical: A-Z",
      sortTitleDesc: "Alphabetical: Z-A",
      viewAll: "View All",
      inStock: "In Stock",
      addToCart: "Add to Cart",
      taxIncluded: "(incl. 8.1% VAT)",
      badgeQuality: "<strong>Swiss Quality Inspected (EN71 / CE)</strong>",
      badgeStock: "<strong>In Stock in Switzerland:</strong> Swiss Post Priority",
      badgeEco: "<strong>Child Safe & Non-Toxic Material</strong>",
      b2bSubheading: "SWISS DIRECT DISPATCH",
      b2bTitle: "Group Rides, Fleet Purchases & Wholesale",
      b2bDesc: "Need fleet bikes or group purchases? We offer fast volume discounts and express Swiss Post delivery.",
      b2bCta: "Inquire Group/Fleet Orders",
      footerBrandDesc: "Switzerland's premier online shop for premium electric motorcycles. Local Swiss warehouse dispatch.",
      footerContactTitle: "Contact & Address",
      footerCountry: "Switzerland",
      footerPhoneLabel: "Phone:",
      footerLegalTitle: "Legal & Compliance",
      footerImp: "Impressum (Legal Notice)",
      footerTerms: "AGB (Terms of Service)",
      footerPriv: "Privacy Policy",
      footerRet: "Return Policy",
      chatSupport: "NeskoShop Support Online",
      page404Title: "Oops! Page not found...",
      page404Desc: "The page you are looking for does not exist or has been moved.",
      page404Home: "Back to Home",
      page404Shop: "Explore Catalog",
      cartDrawerTitle: "YOUR BAG",
      cartShippingQualified: "🎉 You've qualified for Free Standard Shipping!",
      cartUrgencyNotice: "Your items aren't reserved, checkout quickly to make sure you don't miss out.",
      cartBadgeNew: "NEW",
      cartExtraHeading: "ADD A LITTLE EXTRA",
      cartExtraSubtext: "Add one or more of these items to get free delivery",
      cartAddBtn: "+ Add",
      cartDiscountHeading: "DISCOUNT CODE",
      cartDiscountPlaceholder: "Enter code",
      cartApplyBtn: "Apply",
      cartSubtotalLabel: "Subtotal",
      cartCheckoutBtn: "🔒 Checkout securely",
      heroCta2: "Explore Catalog",
      announcementNewsletter: "10% Off Newsletter Signup",
      announcementShipping: "Free Swiss Post Shipping on orders over CHF 80.00",
      productStockBadge: "⚡ In Stock at Zürich Warehouse — Dispatched in 24h",
      productDeliveryText: "<strong>Swiss Post Priority Express:</strong> Order within <strong>3 hrs 15 mins</strong> for dispatch tomorrow to any address in Switzerland.",
      accordionShippingTitle: "Swiss Shipping & Pickup",
      accordionShippingBody: "Free Swiss Post Express Shipping on orders over CHF 100. Standard delivery 1-2 business days. Free pickup at our Zürich warehouse (Industriestrasse 10, 8000 Zürich).",
      accordionReturnsTitle: "30-Day Money-Back Guarantee",
      accordionReturnsBody: "If you are not 100% satisfied with your order, you can return unused items within 30 days.",
      accordionSpecsTitle: "Product Specs & Safety (EN71 / CE)",
      accordionSpecsBody: "Tested to European safety standards (EN71 / CE). Non-toxic, phthalate-free and durable.",
      cartEmptyTitle: "Your bag is empty",
      cartEmptyText: "Add items to your bag to explore our Swiss collection.",
      supportTitle: "💬 Swiss Customer Support",
      supportRecipient: "Direct Email Recipient: <strong style=\"color: #4f46e5;\">info@sefoskis.ch</strong>",
      supportLabelName: "Your Name *",
      supportPlaceholderName: "e.g. Anna Smith",
      supportLabelEmail: "Your Email Address *",
      supportPlaceholderEmail: "your.email@example.ch",
      supportLabelSubject: "Subject / Topic",
      supportOptionGeneral: "General Inquiry",
      supportOptionOrder: "Order Status & Shipping (Swiss Post)",
      supportOptionProduct: "Product Advice & Tech Specs",
      supportOptionReturn: "Returns & Refunds",
      supportOptionBulk: "Bulk Order / Schools / Events",
      supportLabelMessage: "Your Message *",
      supportPlaceholderMessage: "How can we help you?",
      supportSubmitBtn: "✉️ Send message to info@sefoskis.ch",
      supportEmailLink: "Write directly via email: info@sefoskis.ch",
      legalMeta: "Last updated: January 2026 | Swiss Law Compliant (OR / nDSG)",
      legalTitle_impressum: "Impressum (Legal Notice)",
      legalBody_impressum: "<p><strong>Sefoski's GmbH</strong><br>Fährstrasse 33<br>4632 Trimbach<br>Switzerland</p><p><strong>Authorized Representative:</strong><br>Management of Sefoski's GmbH</p><p><strong>Contact:</strong><br>Email: info@sefoskis.ch<br>Phone: +41 76 450 35 13</p><div class=\"legal-policy-box\"><p>Commercial Register: Canton of Solothurn<br>Company Identification Number (UID): CHE-123.456.789<br>VAT Number: CHE-123.456.789 MWST</p></div>",
      legalTitle_terms: "Terms of Service (AGB)",
      legalBody_terms: "<h2>1. Scope</h2><p>These General Terms and Conditions (AGB) apply to all orders placed through the online shop of NeskoShop (Sefoski's GmbH).</p><h2>2. Conclusion of Contract</h2><p>The presentation of products in the online shop does not constitute a legally binding offer, but a non-binding online catalog.</p><h2>3. Prices and Shipping Costs</h2><p>All prices include the statutory Swiss value-added tax (8.1%). Standard shipping via Swiss Post is free for orders over CHF 80.00.</p>",
      legalTitle_privacy: "Privacy Policy",
      legalBody_privacy: "<h2>1. Data Protection at a Glance</h2><p>We take the protection of your personal data very seriously. We treat your personal data confidentially and in accordance with statutory data protection regulations (FADP/nDSG).</p><h2>2. Data Collection on our Website</h2><p>Data processing on this website is carried out by the website operator (Sefoski's GmbH). We use Shopify as our e-commerce platform.</p><div class=\"legal-policy-box\"><p><strong>Payment Processing:</strong> Your payment data is securely processed and encrypted by our payment partners (TWINT, Stripe, PayPal). We do not store credit card data.</p></div>",
      legalTitle_returns: "Return Policy",
      legalBody_returns: "<h2>Right of Withdrawal</h2><p>You have the right to withdraw from this contract within 30 days without giving any reason.</p><h2>Consequences of Withdrawal</h2><p>If you withdraw from this contract, we shall reimburse you for all payments received from you, including delivery costs, without undue delay and no later than 14 days.</p><div class=\"legal-policy-box\"><p><strong>Return Address:</strong><br>Sefoski's GmbH<br>Fährstrasse 33<br>4632 Trimbach<br>Switzerland</p></div>"
    },
    DE: {
      navShop: "Katalog",
      navCategories: "Kategorien",
      navDirect: "Auf Lager",
      navDeals: "Angebote",
      heroTitle: "NEU EINGETROFFEN: ELEKTROMOTORRÄDER",
      heroDesc: "Exklusiv für Sie ausgewählt. Alle Produkte direkt in der Schweiz auf Lager und versandbereit per Swiss Post Priority.",
      heroCta: "Jetzt einkaufen",
      catalogTitle: "NEU EINGETROFFEN: SCHWEIZER SORTIMENT",
      catalogPageTitle: "Produkte",
      catalogPageDesc: "Alle Produkte direkt in der Schweiz auf Lager und versandbereit per Swiss Post Priority.",
      stockFilterLabel: "Auf Lager in der Schweiz",
      sortLabel: "Sortieren:",
      sortFeatured: "Empfohlen",
      sortPriceAsc: "Preis: Niedrig zu Hoch (CHF)",
      sortPriceDesc: "Preis: Hoch zu Niedrig (CHF)",
      sortTitleAsc: "Alphabetisch: A-Z",
      sortTitleDesc: "Alphabetisch: Z-A",
      viewAll: "Alle ansehen",
      inStock: "Auf Lager",
      addToCart: "In den Warenkorb",
      taxIncluded: "(inkl. 8.1% MWST)",
      badgeQuality: "<strong>Schweizer Qualitätsgeprüft (EN71 / CE)</strong>",
      badgeStock: "<strong>Auf Lager in der Schweiz:</strong> Swiss Post Priority",
      badgeEco: "<strong>Ungiftig & Kindersicher Zertifiziert</strong>",
      b2bSubheading: "SCHWEIZER DIREKTVERSAND",
      b2bTitle: "Gruppenkäufe, Flotten & Großbestellungen",
      b2bDesc: "Gruppenkäufe oder Flotten? Wir bieten attraktive Mengenrabatte und schnellen Versand.",
      b2bCta: "Großbestellung anfragen",
      footerBrandDesc: "Schweizers führender Online-Shop für erstklassige Elektromotorräder mit lokaler Lagerhaltung in Zürich.",
      footerContactTitle: "Kontakt & Adresse",
      footerCountry: "Schweiz",
      footerPhoneLabel: "Telefon:",
      footerLegalTitle: "Rechtliches & Compliance",
      footerImp: "Impressum",
      footerTerms: "AGB",
      footerPriv: "Datenschutz",
      footerRet: "Widerrufsrecht",
      chatSupport: "NeskoShop Support Online",
      page404Title: "Ups! Seite nicht gefunden...",
      page404Desc: "Die von Ihnen gesuchte Seite konnte nicht gefunden werden oder wurde verschoben.",
      page404Home: "Zur Startseite",
      page404Shop: "Katalog durchsuchen",
      cartDrawerTitle: "IHRE EINKAUFSTASCHE",
      cartShippingQualified: "🎉 Sie haben sich für den kostenlosen Swiss Post Expressversand qualifiziert!",
      cartUrgencyNotice: "Ihre Artikel sind nicht reserviert. Schließen Sie die Bestellung ab, bevor sie ausverkauft sind.",
      cartBadgeNew: "NEU",
      cartExtraHeading: "DAS GEWISSE EXTRA",
      cartExtraSubtext: "Fügen Sie eines dieser Produkte hinzu, um kostenlosen Versand zu erhalten",
      cartAddBtn: "+ Hinzufügen",
      cartDiscountHeading: "GUTSCHEINCODE",
      cartDiscountPlaceholder: "Code eingeben",
      cartApplyBtn: "Einlösen",
      cartSubtotalLabel: "Zwischensumme",
      cartCheckoutBtn: "🔒 Sicher zur Kasse",
      heroCta2: "Katalog entdecken",
      announcementNewsletter: "10% Rabatt bei Newsletter-Anmeldung",
      announcementShipping: "Kostenloser Swiss Post Versand ab CHF 80.00",
      productStockBadge: "⚡ Auf Lager im Zürich Lager — Versand in 24h",
      productDeliveryText: "<strong>Swiss Post Priority Express:</strong> Innerhalb von <strong>3 Std. 15 Min.</strong> bestellen für Versand morgen an jede Adresse in der Schweiz.",
      accordionShippingTitle: "Schweizer Versand & Abholung",
      accordionShippingBody: "Kostenloser Swiss Post Express Versand ab CHF 100. Standardlieferzeit beträgt 1-2 Werktage. Kostenlose Abholung in unserem Lager in Zürich (Industriestrasse 10, 8000 Zürich).",
      accordionReturnsTitle: "30 Tage Geld-zurück-Garantie",
      accordionReturnsBody: "Wenn Sie mit Ihrer Bestellung nicht 100% zufrieden sind, können Sie unbenutzte Artikel innerhalb von 30 Tagen zurückgeben.",
      accordionSpecsTitle: "Produktspezifikationen & Sicherheit (EN71 / CE)",
      accordionSpecsBody: "Geprüft nach europäischen Sicherheitsnormen (EN71 / CE). Ungiftig, phthalatfrei und langlebig.",
      cartEmptyTitle: "Ihr Warenkorb ist leer",
      cartEmptyText: "Fügen Sie Produkte zum Warenkorb hinzu, um das Schweizer Sortiment zu entdecken.",
      supportTitle: "💬 Schweizer Kundensupport",
      supportRecipient: "Direkter E-Mail-Empfänger: <strong style=\"color: #4f46e5;\">info@sefoskis.ch</strong>",
      supportLabelName: "Ihr Name *",
      supportPlaceholderName: "z.B. Anna Muster",
      supportLabelEmail: "Ihre E-Mail-Adresse *",
      supportPlaceholderEmail: "ihre.email@beispiel.ch",
      supportLabelSubject: "Betreff / Thema",
      supportOptionGeneral: "Allgemeine Anfrage",
      supportOptionOrder: "Bestellstatus & Versand (Swiss Post)",
      supportOptionProduct: "Produktberatung & Technik",
      supportOptionReturn: "Reklamation & Rückgabe",
      supportOptionBulk: "Großbestellung / Schule / Event",
      supportLabelMessage: "Ihre Nachricht *",
      supportPlaceholderMessage: "Wie können wir Ihnen helfen?",
      supportSubmitBtn: "✉️ Nachricht an info@sefoskis.ch senden",
      supportEmailLink: "Direkt per E-Mail schreiben: info@sefoskis.ch",
      legalMeta: "Zuletzt aktualisiert: Januar 2026 | Konform mit Schweizer Recht (OR / nDSG)",
      legalTitle_impressum: "Impressum",
      legalBody_impressum: "<p><strong>Sefoski's GmbH</strong><br>Fährstrasse 33<br>4632 Trimbach<br>Schweiz</p><p><strong>Vertretungsberechtigte Person:</strong><br>Geschäftsführung der Sefoski's GmbH</p><p><strong>Kontakt:</strong><br>E-Mail: info@sefoskis.ch<br>Telefon: 076 450 35 13</p><div class=\"legal-policy-box\"><p>Handelsregisteramt: Kanton Solothurn<br>Unternehmens-Identifikationsnummer (UID): CHE-123.456.789<br>MWST-Nummer: CHE-123.456.789 MWST</p></div>",
      legalTitle_terms: "Allgemeine Geschäftsbedingungen (AGB)",
      legalBody_terms: "<h2>1. Geltungsbereich</h2><p>Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Bestellungen, die über den Online-Shop von NeskoShop (Sefoski's GmbH) getätigt werden.</p><h2>2. Vertragsschluss</h2><p>Die Darstellung der Produkte im Online-Shop stellt kein rechtlich bindendes Angebot, sondern einen unverbindlichen Online-Katalog dar.</p><h2>3. Preise und Versandkosten</h2><p>Alle Preise verstehen sich inklusive der gesetzlichen Schweizer Mehrwertsteuer (8.1%). Ab CHF 80.00 ist der Standardversand via Swiss Post kostenlos.</p>",
      legalTitle_privacy: "Datenschutzerklärung",
      legalBody_privacy: "<h2>1. Datenschutz auf einen Blick</h2><p>Wir nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften (nDSG).</p><h2>2. Datenerfassung auf unserer Website</h2><p>Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber (Sefoski's GmbH). Wir verwenden Shopify als E-Commerce-Plattform.</p><div class=\"legal-policy-box\"><p><strong>Zahlungsabwicklung:</strong> Ihre Zahlungsdaten werden sicher und verschlüsselt von unseren Zahlungspartnern (TWINT, Stripe, PayPal) verarbeitet. Wir speichern keine Kreditkartendaten.</p></div>",
      legalTitle_returns: "Widerrufsrecht",
      legalBody_returns: "<h2>Widerrufsbelehrung</h2><p>Sie haben das Recht, binnen 30 Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen.</p><h2>Folgen des Widerrufs</h2><p>Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen, die wir von Ihnen erhalten haben, einschliesslich der Lieferkosten, unverzüglich und spätestens binnen 14 Tagen zurückzuzahlen.</p><div class=\"legal-policy-box\"><p><strong>Rücksendeadresse:</strong><br>Sefoski's GmbH<br>Fährstrasse 33<br>4632 Trimbach<br>Schweiz</p></div>"
    }
  };
  window.i18nDict = i18nDict;

  function initLanguageSwitcher() {
    var switcherBtn = document.querySelector('[data-lang-toggle]');
    var currentLang = localStorage.getItem('swiss_store_lang') || 'DE';

    function applyLanguage(lang) {
      currentLang = lang;
      localStorage.setItem('swiss_store_lang', lang);

      // Update button active state
      var switcherBtns = document.querySelectorAll('[data-lang-toggle], [data-lang-switcher]');
      switcherBtns.forEach(function(btn) {
        var flagDe = btn.querySelector('[data-lang-flag-de]');
        var flagEn = btn.querySelector('[data-lang-flag-en]');
        if (flagDe && flagEn) {
          if (lang === 'DE') {
            flagDe.classList.add('lang-flag--active');
            flagDe.style.fontWeight = '800';
            flagDe.style.color = '#2563eb';
            flagEn.classList.remove('lang-flag--active');
            flagEn.style.fontWeight = '500';
            flagEn.style.color = '#64748b';
          } else {
            flagEn.classList.add('lang-flag--active');
            flagEn.style.fontWeight = '800';
            flagEn.style.color = '#2563eb';
            flagDe.classList.remove('lang-flag--active');
            flagDe.style.fontWeight = '500';
            flagDe.style.color = '#64748b';
          }
        }
      });

      // Translate all data-i18n elements
      var dict = i18nDict[lang] || i18nDict.DE;
      var els = document.querySelectorAll('[data-i18n]');
      els.forEach(function (el) {
        var key = el.getAttribute('data-i18n');
        if (dict[key]) {
          if (dict[key].indexOf('<') !== -1) {
            el.innerHTML = dict[key];
          } else {
            el.textContent = dict[key];
          }
        }
      });

      // Translate placeholders
      var placeholderEls = document.querySelectorAll('[data-i18n-placeholder]');
      placeholderEls.forEach(function (el) {
        var key = el.getAttribute('data-i18n-placeholder');
        if (dict[key]) {
          el.setAttribute('placeholder', dict[key]);
        }
      });

      document.dispatchEvent(new CustomEvent('languageChanged', { detail: { lang: lang, dict: dict } }));

      if (window.updateCartUI) {
        window.updateCartUI();
      }
    }

    window.setStoreLanguage = function (lang) {
      applyLanguage(lang);
    };

    window.toggleStoreLanguage = function () {
      var nextLang = currentLang === 'DE' ? 'EN' : 'DE';
      applyLanguage(nextLang);
    };

    if (switcherBtn) {
      var flagDe = switcherBtn.querySelector('[data-lang-flag-de]');
      var flagEn = switcherBtn.querySelector('[data-lang-flag-en]');

      if (flagDe) {
        flagDe.addEventListener('click', function (e) {
          e.stopPropagation();
          applyLanguage('DE');
        });
      }
      if (flagEn) {
        flagEn.addEventListener('click', function (e) {
          e.stopPropagation();
          applyLanguage('EN');
        });
      }

      switcherBtn.addEventListener('click', function () {
        window.toggleStoreLanguage();
      });
    }

    // Apply initial language preference
    applyLanguage(currentLang);
  }

  // --------------------------------------------------------------------------
  function sanitizeCartItem(item) {
    if (!item) return null;
    var rawTitle = item.title || 'Produkt 1';
    // Strip out any badge strings ("Auf Lager", "In Stock", "⚡") and newlines
    var cleanTitle = rawTitle.split('\n')[0].replace(/\s*(Auf Lager|In Stock|⚡).*$/i, '').trim();
    if (!cleanTitle) cleanTitle = 'Produkt 1';

    // Normalize id
    var cleanId = item.id || cleanTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    var price = parseFloat(item.price);
    if (isNaN(price) || price <= 0) {
      price = cleanTitle.indexOf('2') !== -1 ? 40.00 : (cleanTitle.indexOf('3') !== -1 ? 60.00 : 20.00);
    }

    return {
      id: cleanId,
      title: cleanTitle,
      price: price,
      weight: parseInt(item.weight, 10) || 1000,
      image: item.image || '',
      quantity: Math.max(1, parseInt(item.quantity, 10) || 1)
    };
  }

  function getTestCart() {
    try {
      var raw = localStorage.getItem('swiss_test_cart');
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      var consolidated = {};
      parsed.forEach(function (i) {
        var clean = sanitizeCartItem(i);
        if (clean) {
          if (consolidated[clean.id]) {
            consolidated[clean.id].quantity += clean.quantity;
          } else {
            consolidated[clean.id] = clean;
          }
        }
      });

      var result = [];
      for (var k in consolidated) {
        result.push(consolidated[k]);
      }
      localStorage.setItem('swiss_test_cart', JSON.stringify(result));
      return result;
    } catch (e) {
      return [];
    }
  }

  function saveTestCart(cart) {
    localStorage.setItem('swiss_test_cart', JSON.stringify(cart));
    updateCartUI();
  }

  function calculateSwissPostRates(subtotal, totalWeightGrams) {
    var threshold = 80.00;
    if (subtotal >= threshold) {
      return {
        isFree: true,
        economy: 0,
        priority: 0,
        express: 16.00,
        weightKg: (totalWeightGrams / 1000).toFixed(2),
        rateLabel: 'Kostenloser Versand (ab CHF 80.00)'
      };
    }

    var weightKg = totalWeightGrams / 1000;
    var economyRate = 8.50;
    var priorityRate = 10.50;
    var expressRate = 16.00;

    if (weightKg > 10) {
      economyRate = 20.50;
      priorityRate = 23.50;
      expressRate = 32.00;
    } else if (weightKg > 2) {
      economyRate = 11.50;
      priorityRate = 14.50;
      expressRate = 22.00;
    }

    return {
      isFree: false,
      economy: economyRate,
      priority: priorityRate,
      express: expressRate,
      weightKg: weightKg.toFixed(2),
      rateLabel: 'Die Post Paket (' + weightKg.toFixed(2) + ' kg)'
    };
  }

  function addTestItemToCart(item) {
    var clean = sanitizeCartItem(item);
    var cart = getTestCart();
    var existing = cart.find(function (i) { return i.id === clean.id || i.title === clean.title; });
    if (existing) {
      existing.quantity += clean.quantity || 1;
    } else {
      cart.push(clean);
    }
    saveTestCart(cart);
    openCartDrawer();
  }

  function updateTestItemQuantity(idOrTitle, change) {
    var cart = getTestCart();
    var item = cart.find(function (i) { return i.id === idOrTitle || i.title === idOrTitle; });
    if (item) {
      item.quantity += change;
      if (item.quantity <= 0) {
        cart = cart.filter(function (i) { return i.id !== item.id; });
      }
      saveTestCart(cart);
    }
  }

  function removeTestItem(idOrTitle) {
    var cart = getTestCart().filter(function (i) { return i.id !== idOrTitle && i.title !== idOrTitle; });
    saveTestCart(cart);
  }

  function openCartDrawer() {
    var drawer = document.querySelector('[data-cart-drawer]');
    var overlay = document.querySelector('[data-cart-drawer-overlay]');
    if (drawer) {
      drawer.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
  }

  function closeCartDrawer() {
    var drawer = document.querySelector('[data-cart-drawer]');
    var overlay = document.querySelector('[data-cart-drawer-overlay]');
    if (drawer) {
      drawer.setAttribute('aria-hidden', 'true');
      if (overlay) overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  }

  function openCheckoutModal() {
    closeCartDrawer();
    var isEN = (localStorage.getItem('swiss_store_lang') === 'EN');
    var cart = getTestCart();
    if (cart.length === 0) {
      alert(isEN ? 'Your bag is empty. Please add an item first.' : 'Ihr Warenkorb ist leer. Bitte fügen Sie zuerst einen Artikel hinzu.');
      return;
    }

    var subtotal = cart.reduce(function (sum, i) { return sum + (i.price * i.quantity); }, 0);
    var totalWeightGrams = cart.reduce(function (sum, i) { return sum + ((i.weight || 500) * i.quantity); }, 0);
    var rates = calculateSwissPostRates(subtotal, totalWeightGrams);
    var shipping = rates.priority;
    var total = subtotal + shipping;

    var existingModal = document.getElementById('test-checkout-modal');
    if (existingModal) existingModal.remove();

    var freeShippingText = isEN ? 'FREE (orders over CHF 80.00)' : 'KOSTENLOS (ab CHF 80.00)';

    var modalHtml = `
      <div id="test-checkout-modal" style="position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(15, 23, 42, 0.75); backdrop-filter: blur(12px); z-index: 10000; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: #ffffff; width: 100%; max-width: 540px; border-radius: 20px; padding: 32px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); max-height: 90vh; overflow-y: auto;">
          <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #e2e8f0; padding-bottom: 16px; margin-bottom: 24px;">
            <h2 style="font-size: 22px; font-weight: 800; color: #0f172a; margin: 0;">${isEN ? '🔒 Checkout' : '🔒 Test-Kasse (Checkout)'}</h2>
            <button type="button" onclick="document.getElementById('test-checkout-modal').remove()" style="background: none; border: none; font-size: 20px; cursor: pointer; color: #64748b;">✕</button>
          </div>
          <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; margin-bottom: 24px;">
            <div style="font-weight: 700; font-size: 15px; margin-bottom: 12px;">${isEN ? 'Order Summary' : 'Bestellübersicht'}</div>
            ${cart.map(function(i){ return `<div style="display:flex; justify-space-between; margin-bottom:6px; font-size:14px;"><span>${i.title} (${i.quantity}x)</span><span style="font-weight:700;">CHF ${(i.price * i.quantity).toFixed(2)}</span></div>`; }).join('')}
            <div style="border-top: 1px solid #cbd5e1; margin-top: 12px; padding-top: 12px; display: flex; justify-content: space-between; font-size: 14px;">
              <span>${isEN ? 'Subtotal:' : 'Zwischensumme:'}</span><span>CHF ${subtotal.toFixed(2)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 13px; color: #64748b; margin-top: 4px;">
              <span>${isEN ? 'Total Weight:' : 'Gesamtgewicht:'}</span><span>${rates.weightKg} kg</span>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 14px; margin-top: 4px;">
              <span>${isEN ? 'Swiss Post Express Shipping:' : 'Die Post Expressversand:'}</span><span style="font-weight:700;">${rates.isFree ? freeShippingText : 'CHF ' + rates.priority.toFixed(2)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 18px; font-weight: 800; color: #4f46e5; margin-top: 12px; padding-top: 12px; border-top: 2px solid #e2e8f0;">
              <span>${isEN ? 'Total (incl. 8.1% VAT):' : 'Gesamtsumme (inkl. 8.1% MWST):'}</span><span>CHF ${total.toFixed(2)}</span>
            </div>
          </div>

          <form onsubmit="event.preventDefault(); window.completeTestOrder();">
            <div style="margin-bottom: 16px;">
              <label style="display: block; font-weight: 700; font-size: 13px; margin-bottom: 6px;">${isEN ? 'Confirmation Email Address' : 'E-Mail-Adresse für Bestätigung'}</label>
              <input type="email" required value="customer@example.ch" style="width: 100%; padding: 10px 14px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 14px;">
            </div>
            <div style="margin-bottom: 16px;">
              <label style="display: block; font-weight: 700; font-size: 13px; margin-bottom: 6px;">${isEN ? 'Shipping Address (Switzerland)' : 'Lieferadresse (Schweiz)'}</label>
              <input type="text" required value="Sample Street 12" style="width: 100%; padding: 10px 14px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 14px; margin-bottom: 8px;">
              <div style="display: grid; grid-template-columns: 1fr 2fr; gap: 8px;">
                <input type="text" required value="8000" style="padding: 10px 14px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 14px;">
                <input type="text" required value="Zurich" style="padding: 10px 14px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 14px;">
              </div>
            </div>
            <button type="submit" class="btn btn-primary" style="width: 100%; padding: 14px; font-size: 16px; margin-top: 8px;">${isEN ? ('Complete Order (CHF ' + total.toFixed(2) + ')') : ('Test-Bestellung kostenpflichtig ausführen (CHF ' + total.toFixed(2) + ')')}</button>
          </form>
        </div>
      </div>
    `;
    document.body.insertAdjacentHTML('beforeend', modalHtml);
  }

  function completeTestOrder() {
    var isEN = (localStorage.getItem('swiss_store_lang') === 'EN');
    var modal = document.getElementById('test-checkout-modal');
    if (modal) modal.remove();
    localStorage.removeItem('swiss_test_cart');
    updateCartUI();
    alert(isEN ? '✅ Order successfully placed! Thank you.' : '✅ Test-Bestellung erfolgreich abgeschlossen! Vielen Dank.');
  }

  function updateCartUI() { return; // Disabled to use native Shopify cart
    var cart = getTestCart();
    var totalCount = cart.reduce(function (sum, i) { return sum + i.quantity; }, 0);
    var subtotal = cart.reduce(function (sum, i) { return sum + (i.price * i.quantity); }, 0);

    // Update count badges
    document.querySelectorAll('[data-cart-count], .header__cart-count, .cart-count, [data-cart-drawer-count]').forEach(function (el) {
      el.textContent = totalCount;
    });

    // Update Drawer Subtotal & Free Shipping Progress Bar
    var drawerBody = document.querySelector('[data-cart-drawer-body]');
    var drawerSubtotal = document.querySelector('[data-cart-subtotal]');
    var drawerFooter = document.querySelector('.cart-drawer__footer');
    var shippingBar = document.querySelector('[data-cart-shipping-bar]');

    if (drawerSubtotal) {
      drawerSubtotal.textContent = 'CHF ' + subtotal.toFixed(2);
    }

    var stickySubtotal = document.querySelector('.cart-sticky-mobile-footer__subtotal, [data-sticky-cart-footer] .cart-sticky-mobile-footer__subtotal');
    if (stickySubtotal) {
      stickySubtotal.textContent = 'CHF ' + subtotal.toFixed(2);
    }

    if (shippingBar) {
      var threshold = 80.00;
      var msgEl = shippingBar.querySelector('.cart-drawer__shipping-message');
      var fillEl = shippingBar.querySelector('.cart-drawer__progress-fill');
      var lang = localStorage.getItem('swiss_store_lang') || 'DE';

      if (subtotal >= threshold) {
        if (msgEl) {
          msgEl.className = 'cart-drawer__shipping-message cart-drawer__shipping-message--success';
          msgEl.innerHTML = lang === 'EN' ? "🎉 You've qualified for Free Standard Shipping!" : '🎉 Sie haben sich für den kostenlosen Swiss Post Expressversand qualifiziert!';
        }
        if (fillEl) fillEl.style.width = '100%';
      } else {
        var remaining = (threshold - subtotal).toFixed(2);
        var percent = Math.min(100, Math.max(0, (subtotal / threshold) * 100));
        if (msgEl) {
          msgEl.className = 'cart-drawer__shipping-message';
          msgEl.innerHTML = lang === 'EN' ? 'Add <strong>CHF ' + remaining + '</strong> more for <strong>Free Shipping</strong> (over CHF 80.00)!' : 'Noch <strong>CHF ' + remaining + '</strong> für <strong>kostenlosen Swiss Post Versand</strong> (ab CHF 80.00)!';
        }
        if (fillEl) fillEl.style.width = percent + '%';
      }
    }

    if (drawerFooter) {
      drawerFooter.style.display = cart.length === 0 ? 'none' : 'block';
    }

    if (drawerBody) {
      if (cart.length === 0) {
        var lang = localStorage.getItem('swiss_store_lang') || 'DE';
        var isEN = (lang === 'EN');
        var emptyTitle = isEN ? 'Your bag is empty' : 'Ihr Warenkorb ist leer';
        var emptyText = isEN ? 'Add items to your bag to explore our Swiss collection.' : 'Fügen Sie Produkte zum Warenkorb hinzu, um das Schweizer Sortiment zu entdecken.';
        var emptyBtn = isEN ? 'Explore Catalog' : 'Katalog durchsuchen';
        drawerBody.innerHTML = `
          <div class="cart-empty-state" style="text-align: center; padding: 48px 24px;">
            <div style="font-size: 48px; margin-bottom: 16px;">🛒</div>
            <h3 id="cartEmptyTitle" data-i18n="cartEmptyTitle" style="font-size: 20px; font-weight: 800; margin-bottom: 8px;">${emptyTitle}</h3>
            <p id="cartEmptyText" data-i18n="cartEmptyText" style="color: #64748b; font-size: 14px; margin-bottom: 24px;">${emptyText}</p>
            <a href="/collections/all" class="btn btn-primary" id="page404Shop" data-i18n="page404Shop" onclick="if(window.closeCartDrawer){window.closeCartDrawer();}">${emptyBtn}</a>
          </div>
        `;
      } else {
        var itemsHtml = cart.map(function (item) {
          var safeTitle = item.title.replace(/"/g, '&quot;');
          return `
            <div class="cart-drawer-item" data-item-id="${item.id}" style="display: flex; gap: 16px; align-items: center; padding: 14px 0; border-bottom: 1px solid #e2e8f0;">
              <div style="width: 56px; height: 56px; background: #f1f5f9; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-weight: 800; color: #94a3b8; font-size: 11px; text-align: center; flex-shrink: 0;">
                ${safeTitle}
              </div>
              <div style="flex: 1;">
                <div style="font-weight: 800; font-size: 15px; color: #0f172a;">${safeTitle}</div>
                <div style="font-size: 14px; font-weight: 800; color: #4f46e5; margin: 2px 0;">CHF ${item.price.toFixed(2)}</div>
                <div style="display: flex; align-items: center; gap: 8px; margin-top: 4px;">
                  <button type="button" class="cart-qty-minus-btn" data-cart-qty-minus="${item.id}" style="width: 28px; height: 28px; border: 1px solid #cbd5e1; background: #ffffff; border-radius: 6px; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center;">-</button>
                  <span style="font-weight: 700; font-size: 14px; width: 20px; text-align: center;">${item.quantity}</span>
                  <button type="button" class="cart-qty-plus-btn" data-cart-qty-plus="${item.id}" style="width: 28px; height: 28px; border: 1px solid #cbd5e1; background: #ffffff; border-radius: 6px; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center;">+</button>
                </div>
              </div>
              <button type="button" class="cart-remove-btn" data-cart-remove="${item.id}" style="background: none; border: none; color: #94a3b8; cursor: pointer; font-size: 18px; padding: 4px;" title="Entfernen">✕</button>
            </div>
          `;
        }).join('');
        drawerBody.innerHTML = '<div class="cart-drawer__items">' + itemsHtml + '</div>';
      }
    }
  }

  function initTestCartListeners() {
    document.addEventListener('click', function (e) {
      // Intercept Quantity Minus Button
      var qtyMinusBtn = e.target.closest('[data-cart-qty-minus], .cart-qty-minus-btn, .cart-qty-minus');
      if (qtyMinusBtn) {
        e.preventDefault();
        var itemId = qtyMinusBtn.getAttribute('data-cart-qty-minus');
        if (!itemId) {
          var itemCard = qtyMinusBtn.closest('.cart-drawer-item');
          if (itemCard) itemId = itemCard.getAttribute('data-item-id');
        }
        if (itemId) updateTestItemQuantity(itemId, -1);
        return;
      }

      // Intercept Quantity Plus Button
      var qtyPlusBtn = e.target.closest('[data-cart-qty-plus], .cart-qty-plus-btn, .cart-qty-plus');
      if (qtyPlusBtn) {
        e.preventDefault();
        var itemId = qtyPlusBtn.getAttribute('data-cart-qty-plus');
        if (!itemId) {
          var itemCard = qtyPlusBtn.closest('.cart-drawer-item');
          if (itemCard) itemId = itemCard.getAttribute('data-item-id');
        }
        if (itemId) updateTestItemQuantity(itemId, 1);
        return;
      }

      // Intercept Item Remove Button
      var removeBtn = e.target.closest('[data-cart-remove], .cart-remove-btn, .cart-item-remove');
      if (removeBtn) {
        e.preventDefault();
        var itemId = removeBtn.getAttribute('data-cart-remove');
        if (!itemId) {
          var itemCard = removeBtn.closest('.cart-drawer-item');
          if (itemCard) itemId = itemCard.getAttribute('data-item-id');
        }
        if (itemId) removeTestItem(itemId);
        return;
      }

      // Intercept Add to Cart buttons
      var addBtn = e.target.closest('.product-card button, .product-card .btn, [data-add-to-cart]');
      if (addBtn) {
        var card = addBtn.closest('.product-card');
        if (card) {
          e.preventDefault();
          var cardTitleAttr = card.getAttribute('data-title');
          var linkTitleEl = card.querySelector('.product-title a');
          var rawTitle = cardTitleAttr || (linkTitleEl ? linkTitleEl.textContent.trim() : 'Produkt 1');
          var title = rawTitle.split('\n')[0].replace(/\s*(Auf Lager|In Stock|⚡).*$/i, '').trim();
          if (!title) title = 'Produkt 1';

          var priceEl = card.querySelector('.product-price');
          var priceText = priceEl ? priceEl.textContent : '20.00';
          var priceMatch = priceText.match(/CHF\s*([\d\.]+)/i);
          var price = priceMatch ? parseFloat(priceMatch[1]) : (title.indexOf('2') !== -1 ? 40.00 : (title.indexOf('3') !== -1 ? 60.00 : 20.00));

          addTestItemToCart({
            title: title,
            price: price,
            quantity: 1
          });
        }
      }

      // Intercept Checkout Buttons
      var checkoutBtn = e.target.closest('.cart-drawer__checkout-btn, [name="checkout"]');
      if (checkoutBtn) {
        e.preventDefault();
        openCheckoutModal();
      }
    });

    updateCartUI();
  }

  window.addTestItemToCart = addTestItemToCart;
  window.updateTestItemQuantity = updateTestItemQuantity;
  window.removeTestItem = removeTestItem;
  window.openCartDrawer = openCartDrawer;
  window.closeCartDrawer = closeCartDrawer;
  window.openCheckoutModal = openCheckoutModal;
  window.completeTestOrder = completeTestOrder;

  // --------------------------------------------------------------------------
  // Initialize
  // --------------------------------------------------------------------------
  function initCollectionSortFilter() {
    var sortSelects = document.querySelectorAll('[data-sort-select], #SortBy, [id^="SortBy-"]');
    var stockFilters = document.querySelectorAll('[data-stock-filter]');

    function sortProducts(sortBy) {
      var grids = document.querySelectorAll('.product-grid');
      grids.forEach(function (grid) {
        var cards = Array.from(grid.querySelectorAll('.product-card'));
        if (cards.length === 0) return;

        cards.sort(function (a, b) {
          var priceA = parseFloat(a.getAttribute('data-price')) || 0;
          var priceB = parseFloat(b.getAttribute('data-price')) || 0;
          var titleA = (a.getAttribute('data-title') || a.querySelector('.product-title')?.textContent || '').toLowerCase();
          var titleB = (b.getAttribute('data-title') || b.querySelector('.product-title')?.textContent || '').toLowerCase();

          if (sortBy === 'price-ascending') return priceA - priceB;
          if (sortBy === 'price-descending') return priceB - priceA;
          if (sortBy === 'title-ascending') return titleA.localeCompare(titleB);
          if (sortBy === 'title-descending') return titleB.localeCompare(titleA);
          return 0;
        });

        cards.forEach(function (card) {
          grid.appendChild(card);
        });
      });
    }

    sortSelects.forEach(function (select) {
      select.addEventListener('change', function (e) {
        var value = e.target.value;
        sortProducts(value);
      });
    });

    stockFilters.forEach(function (chk) {
      chk.addEventListener('change', function (e) {
        var isChecked = e.target.checked;
        var cards = document.querySelectorAll('.product-card');
        cards.forEach(function (card) {
          card.style.display = isChecked ? 'block' : 'none';
        });
      });
    });
  }

  function init() {
    removeNoJs();
    initMobileMenu();
    initProductVariants();
    initCart();
    initCartDrawer();
    initQuickViewModal();
    initPrivacyBanner();
    initCartToast();
    initLanguageSwitcher();
    initQuantityInputs();
    initSmoothScroll();
    initExternalLinks();
    initLazyImages();
    initFaqAccordion();
    initAnnouncementBar();
    initPasswordRecoveryToggle();
    initAddressForms();
    initAllProvinces();
    initNewsletterForms();
    // initTestCartListeners(); // Disabled: Relying on native Shopify cart saving instead
    initCollectionSortFilter();

  // Native Cart Qty Update
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-qty-minus], [data-qty-plus]');
    if (btn) {
      e.preventDefault();
      var key = btn.getAttribute('data-item-key');
      var isPlus = btn.hasAttribute('data-qty-plus');
      var input = btn.parentElement.querySelector('span');
      var currentQty = parseInt(input.textContent);
      var newQty = isPlus ? currentQty + 1 : Math.max(0, currentQty - 1);
      
      input.textContent = newQty;
      btn.style.opacity = '0.5';
      
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: newQty })
      }).then(res => res.json()).then(() => {
        window.location.reload();
      });
    }
  });

  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

